<template>
  <div class="u-m3" v-loading="loading">
    <form @submit.prevent="createPaymentMethod">
      <div :id="paymentElementMountNodeId"></div>
      <div v-if="errorMessage" class="u-warning">
        <i class="material-icons md-text" aria-hidden="true">warning</i> {{ errorMessage }}
      </div>
      <div v-if="authorizationSetup && authorizationSetup.captureAuthorization" class="authorization-checkbox u-mt2">
        <el-checkbox v-model="authorizationConfirmed">
          <div v-html="authorizationSetup.disclosureFirstParty"></div>
        </el-checkbox>
      </div>
      <div class="u-mt3">
        <el-button type="primary" nativeType="submit" :disabled="paymentButtonDisabled">{{ t('payments.save_payment_method') }}</el-button>
      </div>
    </form>
  </div>

</template>

<script>

import { mapActions } from 'vuex'
import { loadStripe } from '@stripe/stripe-js'
import { sanitizeHTML } from 'simple-sanitizer'
import { sleep } from '@shared_src/utils/misc.util'

export default {
  name: 'r-ba-add-payment-method-stripe',
  props: ['insuredId', 'stripeSetupData'],
  data () {
    let rndID = Math.round(Math.random() * 1000).toString()
    return {
      paymentElementMountNodeId: 'payment-element' + rndID,
      stripe: null,
      cardElement: null,
      paymentElement: null,
      elements: null,
      errorMessage: '',
      clientSecret: null,
      submitButtonVisible: false,
      loading: false,
      authorizationSetup: null,
      // The policyholder's own attestation - the ticked box. Named after the payload field it
      // feeds, to keep it distinct from authorizationSetup.captureAuthorization, which is the
      // carrier's config flag for whether an attestation is required at all.
      authorizationConfirmed: false,
    }
  },
  computed: {
    paymentButtonDisabled () {
      // Fail closed: until the authorization config is known we can't tell whether consent is
      // required, so saving stays blocked rather than storing a method without the consent.
      if (!this.authorizationSetup) return true
      // Boolean(): capture_authorization comes from BC and may be null/absent, which would otherwise
      // make this computed return null. It reads as "not disabled" either way, but a gate feeding
      // :disabled should return a real boolean rather than something merely falsy.
      return Boolean(this.authorizationSetup.captureAuthorization) && !this.authorizationConfirmed
    }
  },
  methods: {
    ...mapActions('payments', [
      'retrievePaymentMethodCreationDetails', 'createPaymentMethodPoll', 'getPaymentMethodPoll',
      'getOwnedPaymentMethods', 'recordSavedMethodAuthorization',
      'retrieveSavedMethodAuthorization']),
    async getAuthorizationSetup () {
      // Dedicated endpoint: the consent capability is resolved carrier-wide, so this needs no
      // payment amount, method type or policy. We used to call retrieveConvenienceFee with
      // placeholder values (1000 / 'ach' / no policy) purely because the fee response was the only
      // carrier of capture_authorization and disclosure_first_party.
      let authorizationResponse = await this.retrieveSavedMethodAuthorization()
      let disclosure = authorizationResponse.data.disclosure_first_party
      this.authorizationSetup = {
        captureAuthorization: authorizationResponse.data.capture_authorization,
        // A null disclosure means the gateway could not reach a BriteCore that has this feature;
        // an empty string means the feature is there with no copy configured. Keep the null rather
        // than sanitizing it into '': both render nothing, but the distinction stays readable.
        // The copy is carrier-authored HTML rendered through v-html, so it is sanitized here, at
        // the point it enters the component rather than at the render site - it stays sanitized
        // however it is rendered, without relying on the template to remember.
        disclosureFirstParty: disclosure === null ? null : sanitizeHTML(disclosure),
      }
    },
    async createPaymentMethod () {
      let confirmStripeSetupResult
      if (!this.authorizationSetup) {
        // Setup never loaded, so consent requirements are unknown - don't store a method.
        this.$elAlert(this.t('payments.authorization_disclosure_load_error'), this.t('global.error'))
        return
      }
      this.loading = true
      try {
        confirmStripeSetupResult = await this.stripe.confirmSetup({
          elements: this.elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required'
        })
        if (confirmStripeSetupResult.error) {
          this.loading = false
          console.error('Stripe confirmSetup error:', confirmStripeSetupResult)
          if (confirmStripeSetupResult.error.type === 'validation_error') {
            this.$elAlert(confirmStripeSetupResult.error.message, this.t('global.error'))
          } else {
            this.$elAlert('Error on payment processor side during payment method add!', this.t('global.error'))
          }

          return
        }
      } catch (e) {
        console.error('Error while calling Stripe confirmSetup:', e)
        this.loading = false
        this.errorMessage = 'Generic error during payment method add!'
        return
      }

      if (confirmStripeSetupResult.setupIntent.status !== 'succeeded') {
        this.loading = false
        this.$elAlert('Somethng wend wrong during payment method add! Status is not "succeeded"',
          this.t('global.error'))
        return
      }
      if (this.authorizationSetup && this.authorizationSetup.captureAuthorization) {
        try {
          // Send the policyholder's own attestation. The save gate above guarantees this is true
          // when capture is on; forwarding it (instead of a hardcoded true) keeps the recorded
          // consent tied to what the user actually confirmed.
          await this.recordSavedMethodAuthorization({
            insuredId: this.insuredId,
            authorizationConfirmed: this.authorizationConfirmed,
          })
        } catch (e) {
          this.loading = false
          this.$elAlert(this.t('payments.record_authorization_error'), this.t('global.error'))
          return
        }
      }

      let createPaymentMethodPollResult
      try {
        createPaymentMethodPollResult = await this.createPaymentMethodPoll({
          insured_id: this.insuredId,
          pm_vendor_id: confirmStripeSetupResult.setupIntent.payment_method,
          pm_timestamp: confirmStripeSetupResult.setupIntent.created,
        })
      } catch (e) {
        this.loading = false
        this.$elAlert('Error during Payment method request-poll operation', this.t('global.error'))
        return
      }

      let createPaymentMethodPollResultData = createPaymentMethodPollResult
      this.loading = false
      if (!createPaymentMethodPollResultData.success) {
        this.$elAlert('Unsuccessful call during Payment method request-poll operation', this.t('global.error'))
        return
      }
      // call next method in chain without await
      this.waitForNewPaymentMethod(createPaymentMethodPollResultData.data.poll_id)
    },
    async waitForNewPaymentMethod (pollId) {
      this.loading = true
      let getPaymentMethodPollResult
      let n = 0
      while (n < 30) {
        // 30 calls is something around 90+ seconds poll.
        // If we don't have info in this period of time - something went wrong
        try {
          getPaymentMethodPollResult = await this.getPaymentMethodPoll(pollId)
        } catch (e) {
          this.errorMessage = 'Error during Payment method get-request-poll operation'
          return
        }
        if (getPaymentMethodPollResult.data.payment_method_id) {
          this.$emit('paymentMethodAdded')
          await this.getOwnedPaymentMethods()
          this.loading = false
          return
        }
        await sleep(3000)  // call each 3 seconds, it's very lightweight call
        n = n + 1
      }
      this.loading = false
      this.$elAlert('Failed to retrieve new payment method', this.t('global.error'))
    },
  },
  async mounted () {
    this.loading = true
    try {
      await this.getAuthorizationSetup()
    } catch (e) {
      // authorizationSetup stays null, which keeps the Save button disabled (fail closed).
      console.error('Error during authorization setup:', e)
      this.errorMessage = this.t('payments.authorization_disclosure_load_error')
    }

    try {
      try {
        this.stripe = await loadStripe(this.stripeSetupData.publishable_key, {
          stripeAccount: this.stripeSetupData.stripe_account,
        })
      } catch (error) {
        console.error('Error loading Stripe:', error)
        this.errorMessage = 'Failed to load payment processor.'
        return
      }

      let appearance = { /* appearance */ }
      let options = {
        layout: {
          type: 'tabs',
          defaultCollapsed: false,
        },
        wallets: {
          link: 'never'
        }
      }

      this.clientSecret = this.stripeSetupData.client_secret
      this.elements = this.stripe.elements({clientSecret: this.clientSecret, appearance})
      this.paymentElement = this.elements.create('payment', options)
      await this.paymentElement.mount('#' + this.paymentElementMountNodeId)
      this.submitButtonVisible = true
    } finally {
      this.loading = false
    }
  },
}
</script>

<style lang="scss">
/* Not scoped: the checkbox label is API-provided HTML rendered by Element UI
   (.el-checkbox__label), which sits outside this component's scope. The unique
   .authorization-checkbox wrapper keeps these overrides local. */
.authorization-checkbox .el-checkbox {
  display: flex;
  align-items: flex-start;
  white-space: normal;
}

.authorization-checkbox .el-checkbox__input {
  margin-top: 2px;
}

.authorization-checkbox .el-checkbox__label {
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
}

/* Element UI ships a global `.el-checkbox__label * { white-space: nowrap; overflow: hidden }`
   that clips the API-provided disclosure HTML. Override it for the descendants here. */
.authorization-checkbox .el-checkbox__label * {
  white-space: normal;
  overflow: visible;
  word-break: break-word;
}
</style>
